<template>
  <div>
    <LoadingDataLoader :message="'Preparing print...'" v-if="isLoadingData"></LoadingDataLoader>
    <ErrorOnLoadingDataFeedback v-else-if="errorLoadingData"></ErrorOnLoadingDataFeedback>

    <div id="print-section-container" class="d-none d-print-block">
      <PageTitle class="text-center">{{company.name}}</PageTitle>
      <template v-if="isCashFlow">
        <PageTitle>Cash flow</PageTitle>
        <AnnualCashFlowTable
          dense
          :isModuleSubsidizedFinanceEnabled="isModuleSubsidizedFinanceEnabled"
          :yearsTableHeadings="yearsCashFlowTableHeadings"
          :yearsTableRows="yearsCashFlowTableRows"
        />
      </template>
      <template v-if="isSPF">
        <PageTitle>SP finanziario</PageTitle>
        <AnnualSPFTable
          dense
          :isModuleSubsidizedFinanceEnabled="isModuleSubsidizedFinanceEnabled"
          :yearsTableHeadings="yearsSPFTableHeadings"
          :yearsTableRows="yearsSPFTableRows"
        />
      </template>
      <template v-if="isIncomeStatement">
        <PageTitle>Analisi reddituale</PageTitle>
        <AnnualIncomeStatementTable
          dense
          :isModuleSubsidizedFinanceEnabled="isModuleSubsidizedFinanceEnabled"
          :isModuleProjectsEnabled="isModuleProjectsEnabled"
          :yearsTableHeadings="yearsIncomeStatementTableHeadings"
          :yearsTableRows="yearsIncomeStatementTableRows"
        />
      </template>
      <template v-if="isBusinessPlan">
        <PageTitle class="text-center">Piano previsionale</PageTitle>
        <BusinessPlan
          :company="company"
          :yearsCashFlowTableHeadings="yearsCashFlowTableHeadings"
          :yearsSPFTableHeadings="yearsSPFTableHeadings"
          :yearsIncomeStatementTableHeadings="yearsIncomeStatementTableHeadings"
          :yearsKPITableHeadings="yearsKPITableHeadings"
          :yearsCashFlowTableRows="yearsCashFlowTableRows"
          :yearsSPFTableRows="yearsSPFTableRows"
          :yearsIncomeStatementTableRows="yearsIncomeStatementTableRows"
          :yearsKPITableRows="yearsKPITableRows"
        />
      </template>
    </div>
  </div>
</template>

<script>

import computedFinancialDataMixin from '@/mixins/computedFinancialData.js';
import operativePageMixin from '@/mixins/operativePage.js';
import loadingDataMixin from '@/mixins/loadingData.js';
import formatMoneyFilter from '@/filters/formatMoney.js';
import { companyHasModule } from '@/helpers/modules.js';

export default {
  mixins: [computedFinancialDataMixin, operativePageMixin, loadingDataMixin],
  components: {
    AnnualCashFlowTable: () => import('@/views/components/Operative/OperativeTable/AnnualCashFlowTable.vue'),
    AnnualIncomeStatementTable: () => import('@/views/components/Operative/OperativeTable/AnnualIncomeStatementTable.vue'),
    AnnualSPFTable: () => import('@/views/components/Operative/OperativeTable/AnnualSPFTable.vue'),
    BusinessPlan: () => import('@/views/components/Operative/OperativeTable/BusinessPlan.vue'),
    PageTitle: () => import('@/views/components/Layout/LayoutOperative/PageTitle.vue'),
    ErrorOnLoadingDataFeedback: () => import('@/views/components/Operative/ErrorOnLoadingDataFeedback.vue'),
    LoadingDataLoader: () => import('@/views/components/Operative/LoadingDataLoader.vue'),
  },
  filters: {
    money: formatMoneyFilter,
  },
  data () {
    return {
      yearsCashFlow: [],
      yearsSPF: [],
      yearsIncomeStatement: [],
      yearsKPI: [],
      section: 'cashFlow',
    };
  },
  computed: {
    isModuleSubsidizedFinanceEnabled () {
      return companyHasModule(this.company, 'SUBSIDIZED_FINANCE');
    },
    isModuleProjectsEnabled () {
      return companyHasModule(this.company, 'PROJECTS');
    },
    yearsCashFlowTableHeadings () {
      return this.convertToYearsHeading(this.yearsCashFlow);
    },
    yearsCashFlowTableRows () {
      return this.convertToRowGroupAutomatically(this.yearsCashFlow);
    },
    yearsSPFTableHeadings () {
      return this.convertToYearsHeading(this.yearsSPF);
    },
    yearsSPFTableRows () {
      return this.convertToRowGroupAutomatically(this.yearsSPF);
    },
    yearsIncomeStatementTableHeadings () {
      return this.convertToYearsHeading(this.yearsIncomeStatement);
    },
    yearsIncomeStatementTableRows () {
      return this.convertToRowGroupAutomatically(this.yearsIncomeStatement);
    },
    yearsKPITableHeadings () {
      return this.convertToYearsHeading(this.yearsKPI);
    },
    yearsKPITableRows () {
      return this.convertToRowGroupAutomatically(this.yearsKPI);
    },
    isCashFlow () {
      return this.section && this.section === 'cashFlow';
    },
    isSPF () {
      return this.section && this.section === 'SPF';
    },
    isIncomeStatement () {
      return this.section && this.section === 'incomeStatement';
    },
    isBusinessPlan () {
      return this.section && this.section === 'businessPlan';
    },
  },
  methods: {
    async retrieveCashFlowData () {
      try {
        const res = await this.$api.getComputedCashFlow(this.company?.id);
        this.yearsCashFlow = res?.data?.years || [];
        this.setDataLoaded();
        return true;
      } catch (e) {}
    },
    async retrieveSPFData () {
      try {
        const res = await this.$api.getComputedStatoPatrimonialeFinanziario(this.company?.id);
        this.yearsSPF = res?.data?.years || [];
        this.setDataLoaded();
        return true;
      } catch (e) {}
    },
    async retrieveIncomeStatementData () {
      try {
        const res = await this.$api.getComputedAnalisiReddituale(this.company?.id);
        this.yearsIncomeStatement = res?.data?.years || [];
        this.setDataLoaded();
        return true;
      } catch (e) {}
    },
    async retrieveKPIData () {
      try {
        const res = await this.$api.getKpi(this.company?.id);
        this.yearsKPI = res?.data?.kpi || [];
        this.setDataLoaded();
        return true;
      } catch (e) {}
    },
    async retrieveBusinessPlanData () {
      await this.retrieveCashFlowData();
      await this.retrieveSPFData();
      await this.retrieveIncomeStatementData();
      await this.retrieveKPIData();
      return true;
    },
  },
  async created () {
    window.onafterprint = () => {
      this.$router.go(-1);
    };

    this.section = this.$route.query.section ?? (this.$route.params.section ?? 'businessPlan');
    this.resetErrorOnLoadingData();
    this.setDataLoading();
    switch (this.section) {
    case 'cashFlow':
      await this.retrieveCashFlowData();
      break;
    case 'SPF' :
      await this.retrieveSPFData();
      break;
    case 'incomeStatement' :
      await this.retrieveIncomeStatementData();
      break;
    case 'businessPlan':
      await this.retrieveBusinessPlanData();
      break;
    }
    setTimeout(() => {
      print();
    }, 100);
  },
};

</script>

<style lang="scss">

#print-section-container {
  width: 100%;
  height: auto;

  .print-section {
    page-break-after: always;
  }
}

</style>
